import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, ShieldCheck, Upload } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const TIER_STYLE = {
  platinum: "bg-[#E5E4E2]/10 text-[#E5E4E2] border-[#E5E4E2]/30",
  gold: "bg-[#F5B300]/10 text-[#F5B300] border-[#F5B300]/30",
  silver: "bg-slate-300/10 text-slate-300 border-slate-300/30",
  bronze: "bg-[#CD7F32]/10 text-[#CD7F32] border-[#CD7F32]/30",
};

const ID_TYPES = [
  { id: "passport", label: "Passport" },
  { id: "national_id", label: "National ID card" },
  { id: "driving_license", label: "Driving licence" },
  { id: "aadhaar", label: "Aadhaar (India)" },
];

export const IdentityPanel = () => {
  const [data, setData] = useState(null);
  const [idType, setIdType] = useState("aadhaar");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/org/identity/mine`, headers()).then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(load, []);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/org/identity/upload?id_type=${idType}`, form, headers());
      toast.success("ID submitted — a FUNZ reviewer will verify it shortly.");
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const { verification, trust } = data;

  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="identity-panel">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex items-center gap-2 font-display text-lg font-bold">
          <ShieldCheck className="h-5 w-5 text-[#22C55E]" /> Trust & ID Verification
        </p>
        <span className={`rounded-full border px-3 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${TIER_STYLE[trust.tier]}`} data-testid="trust-tier">
          {trust.tier} · {trust.score}/100
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="trust-parts">
        {[
          { key: "email", label: "Email verified", points: 10 },
          { key: "govt_id", label: "Govt ID verified", points: 50 },
          { key: "professional", label: "Professional cert", points: 30 },
          { key: "business", label: "Business verified", points: 50 },
        ].map((p) => (
          <div key={p.key} className={`rounded-2xl border p-3.5 text-center ${trust.parts[p.key] ? "border-[#22C55E]/30 bg-[#22C55E]/[0.06]" : "border-white/10 bg-[#09090B]/60 opacity-60"}`}>
            <p className={`text-lg font-black ${trust.parts[p.key] ? "text-[#22C55E]" : "text-slate-600"}`}>{trust.parts[p.key] ? "✓" : `+${p.points}`}</p>
            <p className="mt-1 font-jbmono text-[9px] uppercase tracking-widest text-slate-500">{p.label}</p>
          </div>
        ))}
      </div>

      {verification?.status === "verified" ? (
        <p className="mt-4 rounded-2xl bg-[#22C55E]/10 px-4 py-3 text-xs font-bold text-[#22C55E]" data-testid="id-verified">
          ✅ Government ID verified ({verification.id_type.replace("_", " ")}) — your Trust Score includes the +50 boost.
        </p>
      ) : verification?.status === "pending" ? (
        <p className="mt-4 rounded-2xl bg-[#F97316]/10 px-4 py-3 text-xs font-bold text-[#F97316]" data-testid="id-pending">
          ⏳ {verification.id_type.replace("_", " ")} submitted {String(verification.submitted_at).slice(0, 10)} — review in progress.
        </p>
      ) : (
        <div className="mt-4 space-y-3" data-testid="id-upload">
          {verification?.status === "rejected" && (
            <p className="rounded-2xl bg-[#EF4444]/10 px-4 py-3 text-xs font-bold text-[#EF4444]" data-testid="id-rejected">
              ✕ {verification.review_note || "Your ID was not approved — upload a clearer photo and resubmit."}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <select data-testid="id-type" value={idType} onChange={(e) => setIdType(e.target.value)}
              className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white outline-none focus:border-[#7C3AED]/60">
              {ID_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-dashed border-white/20 px-4 py-2.5 text-xs text-slate-400 hover:border-[#06B6D4]/60 hover:text-white">
              <Upload className="h-3.5 w-3.5" /> {file ? file.name : "Choose photo / PDF (max 5 MB)"}
              <input data-testid="id-file" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <button data-testid="id-submit" onClick={upload} disabled={busy || !file}
              className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
              {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : "Submit for verification"}
            </button>
          </div>
          <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
            Reviewed manually by FUNZ — usually within a day
          </p>
        </div>
      )}
    </section>
  );
};
